import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { relativeTime } from '@/lib/format';
import { assignTranscript, dismissTranscript, getRoamSetup, getTranscriptInbox, listCompanies, listDeals } from '@/data/client';
import type { TranscriptInboxItem } from '@/data/types';
import { FormError } from '@/components/crm/Modal';

/* Where meeting transcripts come from, and the ones that need a home. */
export function Integrations() {
  return (
    <div className="crm-list">
      <Card padding={false} className="crm-section crm-settings-card">
        <div className="crm-section__head"><h3>Gemini (Google Meet)</h3><Badge variant="success">Automatic</Badge></div>
        <p className="crm-cell-muted">
          When Gemini takes notes in a Meet call with a client's people, the notes Doc attached to the calendar event is added to that client's
          Client Vault as a transcript within about 30 minutes of the meeting ending. It works for each person whose Google connection includes Drive
          (Today → Google). Nothing to set up here.
        </p>
      </Card>
      <RoamSetup />
      <Inbox />
    </div>
  );
}

function RoamSetup() {
  const { data } = useQuery({ queryKey: ['roamSetup'], queryFn: getRoamSetup });
  const [copied, setCopied] = useState(false);
  if (!data) return null;
  const ready = data.accessTokenSet && data.signingSecretSet;
  return (
    <Card padding={false} className="crm-section crm-settings-card">
      <div className="crm-section__head"><h3>Roam (Magic Minutes)</h3><Badge variant={ready ? 'success' : 'warning'}>{ready ? 'Connected' : 'Setup needed'}</Badge></div>
      <p className="crm-cell-muted">Roam sends each saved transcript here; it's matched to the client by who was on the call. Anything that matches no client waits below.</p>
      <ol className="crm-steps">
        <li>
          <span>In Roam Admin → Developer → Webhooks, add this URL and subscribe to <code>transcript:saved</code>:</span>
          <div className="crm-addopt">
            <input className="tr-input" readOnly value={data.webhookUrl} onFocus={(e) => e.target.select()} />
            <Button size="sm" variant="outline" onClick={async () => { await navigator.clipboard.writeText(data.webhookUrl); setCopied(true); }}>{copied ? 'Copied' : 'Copy'}</Button>
          </div>
        </li>
        <li>
          <span>Add Worker secrets in Cloudflare (Workers → clientiq → Settings → Variables and Secrets):</span>
          <ul className="crm-check">
            <li className={data.accessTokenSet ? 'is-ok' : ''}><Icon name={data.accessTokenSet ? 'check-circle' : 'alert-circle'} size={14} /> <code>ROAM_ACCESS_TOKEN</code> — your Roam API key</li>
            <li className={data.signingSecretSet ? 'is-ok' : ''}><Icon name={data.signingSecretSet ? 'check-circle' : 'alert-circle'} size={14} /> <code>ROAM_SIGNING_SECRET</code> — the webhook's signing secret (starts with <code>whsec_</code>), shown when you create the webhook</li>
          </ul>
        </li>
      </ol>
    </Card>
  );
}

function Inbox() {
  const { data: items = [] } = useQuery({ queryKey: ['transcriptInbox'], queryFn: getTranscriptInbox });
  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head"><h3>Transcripts to assign</h3>{items.length > 0 && <Badge variant="accent">{items.length}</Badge>}</div>
      {items.length
        ? <ul className="crm-people">{items.map((t) => <InboxItem key={t.id} item={t} />)}</ul>
        : <p className="crm-cell-muted">Nothing waiting. Transcripts that match no client (no known contact or company domain on the call) show here.</p>}
    </Card>
  );
}

function InboxItem({ item }: { item: TranscriptInboxItem }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [dealId, setDealId] = useState('');
  const [open, setOpen] = useState(false);
  const { data: companies = [] } = useQuery({ queryKey: ['companies', { q, view: '' }], queryFn: () => listCompanies({ q }), enabled: q.trim().length > 1 });
  const { data: deals = [] } = useQuery({ queryKey: ['deals', { companyId }], queryFn: () => listDeals({ companyId }), enabled: !!companyId });
  const done = () => { for (const k of ['transcriptInbox', 'company', 'deal']) void qc.invalidateQueries({ queryKey: [k] }); };
  const assign = useMutation({ mutationFn: () => assignTranscript(item.id, companyId, dealId || undefined), onSuccess: done });
  const dismiss = useMutation({ mutationFn: () => dismissTranscript(item.id), onSuccess: done });
  const who = item.participants.map((p) => p.name ?? p.email).filter(Boolean).join(', ');
  return (
    <li style={{ display: 'block' }}>
      <div className="crm-section__head" style={{ marginBottom: 6 }}>
        <div>
          <strong>{item.title}</strong>
          <span className="crm-cell-sub">{item.provider === 'roam' ? 'Roam' : item.provider} · {relativeTime(item.startedAt ?? item.createdAt)}{who ? ` · ${who}` : ''}</span>
        </div>
        <div className="crm-import-choice">
          <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}>{open ? 'Hide' : 'Preview'}</Button>
          <Button size="sm" variant="ghost" onClick={() => dismiss.mutate()} disabled={dismiss.isPending}>Not a client call</Button>
        </div>
      </div>
      {open && <p className="crm-timeline__note crm-preview">{item.preview}</p>}
      <div className="crm-addopt">
        <input className="tr-input" placeholder="Find the company…" value={q} onChange={(e) => { setQ(e.target.value); setCompanyId(''); }} />
        <select className="tr-input" value={companyId} onChange={(e) => { setCompanyId(e.target.value); setDealId(''); }} disabled={!companies.length}>
          <option value="">{q.trim().length > 1 ? `${companies.length} match${companies.length === 1 ? '' : 'es'}` : 'Type to search'}</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {deals.length > 0 && (
          <select className="tr-input" value={dealId} onChange={(e) => setDealId(e.target.value)}>
            <option value="">No specific deal</option>
            {deals.map((d) => <option key={d.id} value={d.id}>{d.name} · {d.stageName}</option>)}
          </select>
        )}
        <Button size="sm" onClick={() => assign.mutate()} disabled={!companyId || assign.isPending}>Add to Vault</Button>
      </div>
      <FormError error={assign.error ?? dismiss.error} />
    </li>
  );
}
